/**
 * Estado de preferencias por habitacion (Block 3 del form offline).
 *
 * Se separa del componente para poder testear la logica sin instanciar todo
 * el `FormOfflineComponent` (que depende de dialog, servicios HTTP y spinner).
 */

/**
 * Sub-grupo `preferences` de una habitacion del FormArray `room`.
 */
export interface RoomPreferencesValue {
  meals?: string | null;
  category?: string | null;
  type_bed?: string | null;
  cancellationPolicy?: string | null;
}

/**
 * Preferencias que se pueden cargar por habitacion.
 */
export const ROOM_PREFERENCE_KEYS: (keyof RoomPreferencesValue)[] = [
  'meals',
  'category',
  'type_bed',
  'cancellationPolicy'
];

/**
 * Total de preferencias por habitacion.
 */
export const ROOM_PREFERENCES_TOTAL = ROOM_PREFERENCE_KEYS.length;

/**
 * Una preferencia esta cargada cuando tiene un valor real. `null`, `undefined`
 * y `''` se consideran vacias: el radio de politica de cancelacion usa `''`
 * para la opcion "sin politica".
 */
export function isPreferenceFilled(value: unknown): boolean {
  return value !== null && value !== undefined && value !== '';
}

/**
 * Cantidad de preferencias cargadas en una habitacion.
 */
export function countRoomPreferences(preferences: RoomPreferencesValue | null | undefined): number {
  if (!preferences) {
    return 0;
  }
  return ROOM_PREFERENCE_KEYS.filter(key => isPreferenceFilled(preferences[key])).length;
}

/**
 * La habitacion ya tiene al menos una preferencia cargada (es decir, fue
 * modificada por el usuario o vino precargada).
 */
export function hasRoomPreferences(preferences: RoomPreferencesValue | null | undefined): boolean {
  return countRoomPreferences(preferences) > 0;
}

/**
 * Texto accesible del estado de preferencias de una habitacion.
 */
export function roomPreferencesStatusLabel(count: number, total: number = ROOM_PREFERENCES_TOTAL): string {
  if (count <= 0) {
    return 'Sin preferencias cargadas';
  }
  return `${count} de ${total} preferencias cargadas`;
}
